<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useChatStore } from '@/stores/chat'
import { useAiChatStore } from '@/stores/aiChat'
import { useChatStream } from '@/composables/useChatStream'
import { X, MessageCircle } from 'lucide-vue-next'
import ChatQueue from './ChatQueue.vue'
import ChatConversation from './ChatConversation.vue'

const chatStore = useChatStore()
const aiChatStore = useAiChatStore()

watch(() => chatStore.isOpen, (open) => {
  if (open) aiChatStore.isOpen = false
})

const {
  onChatQueued,
  onVisitorMessage,
  onAgentMessage,
  onChatEnded,
  onChatJoined,
  setFallback,
} = useChatStream()

// Real-time event handlers
onChatQueued((data) => {
  chatStore.updateSessionFromEvent({
    id: data.sessionId || data.id,
    status: 'queued',
    visitor_name: data.visitor_name || data.visitorName,
    visitor_email: data.visitor_email || data.visitorEmail,
    client_id: data.client_id || data.clientId,
    client_name: data.client_name || data.clientName,
    created_at: data.created_at || new Date().toISOString(),
    session_id: data.session_id || data.sessionId,
    agent_id: null,
    started_at: null,
    ended_at: null,
    metadata: null,
  })
})

onVisitorMessage((data) => {
  chatStore.addIncomingMessage({
    id: data.id || data.messageId || crypto.randomUUID(),
    session_id: data.session_id || data.sessionId,
    sender_type: 'visitor',
    sender_id: null,
    message: data.message,
    created_at: data.created_at || new Date().toISOString(),
  })
})

onAgentMessage((data) => {
  chatStore.addIncomingMessage({
    id: data.id || data.messageId || crypto.randomUUID(),
    session_id: data.session_id || data.sessionId,
    sender_type: 'agent',
    sender_id: data.sender_id || data.agentId,
    message: data.message,
    created_at: data.created_at || new Date().toISOString(),
  })
})

onChatJoined((data) => {
  const d = data as { sessionId?: string; id?: string; agent_id?: number; agentId?: number; agent_first_name?: string; agentName?: string }
  chatStore.updateSessionFromEvent({
    id: (d.sessionId || d.id) as string,
    status: 'active',
    agent_id: d.agent_id || d.agentId,
    agent_first_name: d.agent_first_name || d.agentName,
  })
})

onChatEnded((data) => {
  const d = data as { sessionId?: string; id?: string }
  chatStore.updateSessionFromEvent({
    id: (d.sessionId || d.id) as string,
    status: 'ended',
  })
})

// Polling fallback
setFallback(() => chatStore.fetchSessions(), 10000)

onMounted(() => {
  chatStore.fetchSessions()
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="chatStore.isOpen"
        class="fixed bottom-0 right-0 sm:bottom-24 sm:right-6 z-50 w-full h-full sm:w-[420px] sm:h-[550px] sm:rounded-xl bg-surface-900 border border-surface-700 shadow-2xl flex flex-col overflow-hidden"
      >
        <!-- Panel Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-surface-700 bg-surface-800">
          <div class="flex items-center gap-2">
            <MessageCircle class="w-5 h-5 text-primary-400" />
            <h2 class="text-sm font-semibold text-surface-100">Live Chat</h2>
            <span v-if="chatStore.unreadCount" class="px-1.5 py-0.5 text-xs bg-warning-500/20 text-warning-400 rounded-full">
              {{ chatStore.unreadCount }}
            </span>
          </div>
          <button @click="chatStore.toggle()" class="p-1 text-surface-400 hover:text-surface-200 rounded transition-colors">
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- Body: Queue + Conversation split -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Left: Queue list -->
          <div class="w-[160px] border-r border-surface-700 overflow-y-auto flex-shrink-0">
            <ChatQueue />
          </div>

          <!-- Right: Conversation -->
          <div class="flex-1 flex flex-col overflow-hidden">
            <ChatConversation />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
